
import { useState } from 'react';
import {
  Navigate,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { Boxes, Eye, EyeOff, LockKeyhole, UserRound } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import './LoginPage.css';

export default function LoginPage() {
  const { login, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Admin destination remains unchanged.
  // Distributors now use /user instead of /dashboard.
  const getDestination = (role) =>
    role === 'super_admin' ? '/admin' : '/user';

  if (isAuthenticated) {
    return (
      <Navigate
        to={getDestination(user.role)}
        replace
      />
    );
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);

    try {
      const loggedInUser = await login(
        username.trim(),
        password
      );

      const destination = getDestination(loggedInUser.role);
      const requestedPath = location.state?.from?.pathname;

      // Preserve admin routing and validate distributor routes.
      const validRequestedPath =
        loggedInUser.role === 'super_admin'
          ? requestedPath?.startsWith('/admin')
          : requestedPath?.startsWith('/user');

      navigate(
        validRequestedPath ? requestedPath : destination,
        { replace: true }
      );
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          'Unable to log in. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="login-page">
      <section className="login-panel">
        <div className="login-brand">
          <div className="brand-icon">
            <Boxes size={27} />
          </div>
          <span>StockFlow</span>
        </div>

        <div className="login-heading">
          <p className="eyebrow"> HARSHA INVENTORY MANAGEMENT</p>
          <h1>Welcome back</h1>
          <p>Sign in to manage your inventory and business.</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {error && (
            <div className="login-error" role="alert">
              {error}
            </div>
          )}

          <label htmlFor="username">Username</label>
          <div className="login-input-wrap">
            <UserRound size={19} aria-hidden="true" />
            <input
              id="username"
              type="text"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              placeholder="Enter your username"
              autoComplete="username"
              maxLength={100}
              required
              disabled={loading}
            />
          </div>

          <label htmlFor="password">Password</label>
          <div className="login-input-wrap">
            <LockKeyhole size={19} aria-hidden="true" />
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="Enter your password"
              autoComplete="current-password"
              maxLength={128}
              required
              disabled={loading}
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword((value) => !value)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={19} /> : <Eye size={19} />}
            </button>
          </div>

          <button
            className="login-submit"
            type="submit"
            disabled={loading}
          >
            {loading ? 'Signing in...' : 'Sign in'}
          </button>
        </form>

        <p className="login-footer">
          Secure access to your inventory workspace.
        </p>
      </section>

      <aside className="login-visual">
        <div className="visual-content">
          <div className="visual-tag">ONE WORKSPACE. TOTAL CONTROL.</div>
          <h2>Your inventory,<br />under control.</h2>
          <p>
            Manage products, monitor stock, track batches, and
            keep your business operations organized in one place with harsha It .
          </p>

          <div className="visual-features">
            <div><Boxes size={19} /> Inventory visibility</div>
            <div><LockKeyhole size={19} /> Role-based access</div>
            <div><UserRound size={19} />  Harsha Multi-firm management</div>
          </div>
        </div>
        <span className="visual-caption">
           Harsha INVENTORY MANAGEMENT PLATFORM
        </span>
      </aside>
    </main>
  );
}
